import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './measurements-qc.scss';
import DataGrid, { Column, Button as Butt, Editing, Item, KeyboardNavigation, Paging, Scrolling, Sorting, StateStoring, SearchPanel, Selection, FilterRow, HeaderFilter } from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import { Tooltip } from 'devextreme-react';
import { useApiMeasurementsQC } from '../../api/api.measurementsQC';
import { useApiConfigQC } from '../../api/api.configQC';
import { MedicionQcDTO, CreateMedicionQcDTO, ConfiguracionQcDTO } from '../../types/types.qualityControl';
import { useAppSelector } from '../../contexts/hooks';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { newToast } from '../../components';
import SelectBox from 'devextreme-react/select-box';
import { Chart, Series, ArgumentAxis, ValueAxis, Legend, Tooltip as ChartTooltip, CommonSeriesSettings, Label, Point } from 'devextreme-react/chart';
import { Popup } from 'devextreme-react/popup';
import NumberBox from 'devextreme-react/number-box';
import TextArea from 'devextreme-react/text-area';
import TextBox, { Button as TextBoxButton } from 'devextreme-react/text-box';
import ValidationGroup from 'devextreme-react/validation-group';
import { Validator, RequiredRule, RangeRule } from 'devextreme-react/validator';
import ScrollView from 'devextreme-react/scroll-view';
import { Toolbar } from 'devextreme-react/toolbar';
import { Item as ToolbarItem } from 'devextreme-react/toolbar';
import { resGridStatus3, resButtonsUnvalid, resButtonRefresh, AddIcon } from '../../images/icons';

const MeasurementsQC = () => {
  // Permisos
  const Prm_QC_R = useAppSelector((x) => x.userInfo.Permissions?.Prm_QC_R) ?? false;
  const Prm_QC_C = useAppSelector((x) => x.userInfo.Permissions?.Prm_QC_C) ?? false;
  const Prm_QC_Validar = useAppSelector((x) => x.userInfo.Permissions?.Prm_QC_Validar) ?? false;
  const userId = useAppSelector((x) => x.userInfo.User?.Us_Id) ?? 0;
  const userLocation = Number(useAppSelector((x) => x.userInfo.Location) ?? 0);

  // API Hooks
  const { registerMeasurement, getMeasurementsByConfiguration, getPendingReviewMeasurements, validateMeasurement, rejectMeasurement, getLeveyJenningsData } = useApiMeasurementsQC();
  const { getConfigurationsBySede } = useApiConfigQC();

  // Referencias
  const measurementsGridRef = useRef<DataGrid>(null);

  // Estados
  const [configuraciones, setConfiguraciones] = useState<ConfiguracionQcDTO[]>([]);
  const [selectedConfiguration, setSelectedConfiguration] = useState<ConfiguracionQcDTO | null>(null);
  const [chartData, setChartData] = useState<any>(null);
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  // Modal estados
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [medicionValor, setMedicionValor] = useState<number | null>(null);
  const [medicionObservacion, setMedicionObservacion] = useState('');
  const validationGroupRef = useRef<ValidationGroup>(null);

  // Search state
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    if (Prm_QC_R) {
      loadConfigurations();
    }
  }, [userLocation]);

  useEffect(() => {
    if (selectedConfiguration) {
      loadChartData();
    }
  }, [selectedConfiguration]);

  const loadConfigurations = async () => {
    try {
      const configs = await getConfigurationsBySede(userLocation, true);
      setConfiguraciones(configs || []);
    } catch (error) {
      console.error('Error cargando configuraciones:', error);
      newToast('ERROR', 'Error al cargar configuraciones activas', 5);
    }
  };

  const loadChartData = async () => {
    if (!selectedConfiguration) return;

    try {
      const data = await getLeveyJenningsData(selectedConfiguration.Qcc_Id);
      setChartData(data || []);
    } catch (error) {
      console.error('Error cargando datos del gráfico:', error);
    }
  };

  // ============================================
  // MEDICIONES - DATA SOURCE
  // ============================================
  const measurementsDataSource = useMemo(() => {
    const customStore = new CustomStore({
      key: 'Qcme_Id',
      load: () => {
        if (!selectedConfiguration) {
          return Promise.resolve({ data: [], totalCount: 0 });
        }

        const loadPromise = showPendingOnly ? getPendingReviewMeasurements(userLocation, selectedConfiguration.Qcc_An_Id) : getMeasurementsByConfiguration(selectedConfiguration.Qcc_Id);

        return loadPromise
          .then((res) => ({
            data: res || [],
            totalCount: (res || []).length,
          }))
          .catch(() => {
            throw new Error('Error al cargar mediciones');
          });
      },
    });

    return new DataSource({
      store: customStore,
      reshapeOnPush: true,
    });
  }, [selectedConfiguration, showPendingOnly, userLocation]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleRefresh = useCallback(() => {
    measurementsGridRef.current?.instance.refresh();
    if (selectedConfiguration) {
      loadChartData();
    }
  }, [selectedConfiguration]);

  const handleConfigurationChange = useCallback((config: ConfiguracionQcDTO) => {
    setSelectedConfiguration(config);
    setShowPendingOnly(false);
  }, []);

  const handleTogglePendingOnly = useCallback(() => {
    setShowPendingOnly((prev) => !prev);
  }, []);

  const handleOpenRegisterModal = useCallback(() => {
    if (!selectedConfiguration) {
      newToast('WARNING', 'Debe seleccionar una configuración primero', 5);
      return;
    }

    if (!Prm_QC_C) {
      newToast('WARNING', 'No tiene permisos para registrar mediciones', 5);
      return;
    }

    setMedicionValor(null);
    setMedicionObservacion('');
    setShowRegisterModal(true);
  }, [selectedConfiguration, Prm_QC_C]);

  const handleCloseRegisterModal = useCallback(() => {
    setShowRegisterModal(false);
    setMedicionValor(null);
    setMedicionObservacion('');
  }, []);

  const handleRegisterMeasurement = useCallback(async () => {
    const validationResult = validationGroupRef.current?.instance.validate();
    if (!validationResult?.isValid) {
      return;
    }

    if (isRegistering || !medicionValor) return;

    setIsRegistering(true);
    try {
      const medicion: Omit<CreateMedicionQcDTO, 'Qcme_Us_Id'> = {
        Qcme_Qcc_Id: selectedConfiguration!.Qcc_Id,
        Qcme_Valor: medicionValor,
      };

      const result = await registerMeasurement(medicion);

      // Cerrar modal primero
      handleCloseRegisterModal();

      // Mostrar toast de éxito
      newToast('SUCCESS', '✓ Medición registrada exitosamente', 4);

      // Refrescar grid y gráfico
      await measurementsGridRef.current?.instance.refresh();
      await loadChartData();

    } catch (error: any) {
      console.error('Error registrando medición:', error);
      newToast('ERROR', `Error al registrar: ${error?.message || 'Error desconocido'}`, 5);
    } finally {
      setIsRegistering(false);
    }
  }, [selectedConfiguration, medicionValor, isRegistering, loadChartData, handleCloseRegisterModal]);

  const handleValidateMeasurement = useCallback(async () => {
    if (!Prm_QC_Validar) {
      newToast('WARNING', 'No tiene permisos para validar mediciones', 5);
      return;
    }

    const selectedRows = measurementsGridRef.current?.instance.getSelectedRowsData() as MedicionQcDTO[];
    if (!selectedRows || selectedRows.length === 0) {
      newToast('INFO', 'Debe seleccionar al menos una medición', 5);
      return;
    }

    const pendingRows = selectedRows.filter((x) => x.Qcme_Estado === 1);
    if (pendingRows.length === 0) {
      newToast('INFO', 'Solo se pueden validar mediciones pendientes', 5);
      return;
    }

    try {
      for (const row of pendingRows) {
        await validateMeasurement(row.Qcme_Id, '');
      }
      newToast('SUCCESS', `${pendingRows.length} medición(es) validada(s)`, 3);
      handleRefresh();
    } catch (error) {
      console.error('Error validando mediciones:', error);
    }
  }, [Prm_QC_Validar, handleRefresh]);

  const handleRejectMeasurement = useCallback(async () => {
    if (!Prm_QC_Validar) {
      newToast('WARNING', 'No tiene permisos para rechazar mediciones', 5);
      return;
    }

    const selectedRows = measurementsGridRef.current?.instance.getSelectedRowsData() as MedicionQcDTO[];
    if (!selectedRows || selectedRows.length === 0) {
      newToast('INFO', 'Debe seleccionar al menos una medición', 5);
      return;
    }

    const motivoRechazo = prompt('Ingrese el motivo del rechazo:');
    if (!motivoRechazo) return;

    try {
      for (const row of selectedRows) {
        await rejectMeasurement(row.Qcme_Id, motivoRechazo);
      }
      newToast('SUCCESS', `${selectedRows.length} medición(es) rechazada(s)`, 3);
      handleRefresh();
    } catch (error) {
      console.error('Error rechazando mediciones:', error);
    }
  }, [Prm_QC_Validar, handleRefresh]);

  // ============================================
  // CELL RENDER FUNCTIONS
  // ============================================
  const renderEstadoCelda = useCallback((cellData: any) => {
    const estado = cellData.value;
    let texto = '';
    let color = '';

    switch (estado) {
      case 1: // PENDIENTE
        texto = 'PENDIENTE';
        color = '#FFA500';
        break;
      case 2: // VALIDADO
        texto = 'VALIDADO';
        color = '#4CAF50';
        break;
      case 3: // RECHAZADO
        texto = 'RECHAZADO';
        color = '#F44336';
        break;
      case 4: // ALERTA
        texto = 'ALERTA';
        color = '#FF5722';
        break;
      default:
        texto = 'DESCONOCIDO';
        color = '#9E9E9E';
    }

    return (
      <div
        style={{
          backgroundColor: color,
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: '11px',
        }}>
        {texto}
      </div>
    );
  }, []);

  const renderViolacionCelda = useCallback((cellData: any) => {
    if (!cellData.data.TieneViolaciones) {
      return <div style={{ textAlign: 'center' }}>-</div>;
    }

    return (
      <div
        style={{
          backgroundColor: '#FF5722',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: '11px',
        }}>
        SÍ
      </div>
    );
  }, []);

  if (!Prm_QC_R) {
    return (
      <div className='view crm-contact-list'>
        <div className='view-wrapper'>
          <h3>No tiene permisos para acceder a este módulo</h3>
        </div>
      </div>
    );
  }

  return (
    <div className='view crm-contact-list'>
      <div className='view-wrapper view-wrapper-measurements-qc'>
        <ScrollView width='100%' height='100%' showScrollbar='onScroll'>
          <div className='measurements-header'>
            {/* Título y Selector en la misma línea */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '15px' }}>
              <h2 className='content-block' style={{ margin: 0 }}>Registro de Mediciones</h2>
              <div className='selector-group'>
                <SelectBox
                  dataSource={configuraciones}
                  displayExpr={(item: ConfiguracionQcDTO | null) => {
                    if (!item) return '';
                    return `${item.MaterialNombre || ''} - ${item.AnalitoNombre || ''} - Lote: ${item.LoteCodigo || ''}`;
                  }}
                  valueExpr='Qcc_Id'
                  value={selectedConfiguration?.Qcc_Id || null}
                  onValueChanged={(e) => {
                    if (!e.value) {
                      setSelectedConfiguration(null);
                      return;
                    }
                    const config = configuraciones.find((c) => c.Qcc_Id === e.value);
                    if (config) handleConfigurationChange(config);
                  }}
                  placeholder='Seleccione una configuración...'
                  searchEnabled={true}
                  showClearButton={true}
                  width={600}
                />
              </div>
            </div>

            {/* Información de la configuración seleccionada */}
            {selectedConfiguration && (
              <div className='configuration-selector'>
                <div className='config-info'>
                  <div className='info-item'>
                    <strong>Analito:</strong> {selectedConfiguration.AnalitoNombre}
                  </div>
                  <div className='info-item'>
                    <strong>Media Esperada:</strong> {selectedConfiguration.Qcc_MediaEsperada?.toFixed(2)}
                  </div>
                  <div className='info-item'>
                    <strong>Desv. Estándar:</strong> {selectedConfiguration.Qcc_DesviacionEst?.toFixed(2)}
                  </div>
                  <div className='info-item'>
                    <strong>N:</strong> {selectedConfiguration.Qcc_N}
                  </div>
                  <div className='info-item'>
                    <strong>Mediciones:</strong> {selectedConfiguration.CantidadMediciones || 0}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Toolbar de Acciones */}
          <div className='measurements-toolbar'>
            <Toolbar>
              <ToolbarItem location='before' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                <Button id='RegistrarMedicion' icon={AddIcon} text='Registrar Medición' stylingMode='contained' type='default' onClick={handleOpenRegisterModal} disabled={!selectedConfiguration || isRegistering} />
                <Tooltip target={'#RegistrarMedicion'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Registrar nueva medición
                </Tooltip>
              </ToolbarItem>
              <ToolbarItem location='before' locateInMenu='auto'>
                <div className='separator' />
              </ToolbarItem>
              <ToolbarItem location='before' locateInMenu='auto' showText='inMenu' widget='dxButton' visible={Prm_QC_Validar}>
                <Button id='ValidarMedicion' icon={resGridStatus3} text='Validar' stylingMode='text' onClick={handleValidateMeasurement} disabled={!selectedConfiguration} />
                <Tooltip target={'#ValidarMedicion'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Validar mediciones seleccionadas
                </Tooltip>
              </ToolbarItem>
              <ToolbarItem location='before' locateInMenu='auto' showText='inMenu' widget='dxButton' visible={Prm_QC_Validar}>
                <Button id='RechazarMedicion' icon={resButtonsUnvalid} text='Rechazar' stylingMode='text' onClick={handleRejectMeasurement} disabled={!selectedConfiguration} />
                <Tooltip target={'#RechazarMedicion'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Rechazar mediciones seleccionadas
                </Tooltip>
              </ToolbarItem>
              <ToolbarItem location='before' locateInMenu='auto'>
                <div className='separator' />
              </ToolbarItem>
              {/* <ToolbarItem location='before' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                <Button id='FiltrarPendientes' icon='filter' text={showPendingOnly ? 'Ver Todas' : 'Solo Pendientes'} stylingMode='text' onClick={handleTogglePendingOnly} disabled={!selectedConfiguration} />
                <Tooltip target={'#FiltrarPendientes'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  {showPendingOnly ? 'Mostrar todas las mediciones' : 'Mostrar solo pendientes'}
                </Tooltip>
              </ToolbarItem> */}
              <ToolbarItem location='after' locateInMenu='never'>
                <div style={{ width: '250px', marginRight: '10px' }}>
                  <TextBox
                    placeholder='Buscar...'
                    value={searchValue}
                    onValueChanged={(e) => {
                      setSearchValue(e.value);
                      measurementsGridRef.current?.instance.searchByText(e.value);
                    }}
                    showClearButton={true}
                    stylingMode='outlined'></TextBox>
                </div>
              </ToolbarItem>
              <ToolbarItem location='after' locateInMenu='auto'>
                <div className='separator' />
              </ToolbarItem>
              <ToolbarItem location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                <Button id='ActualizarMediciones' icon={resButtonRefresh} text='Actualizar' stylingMode='text' onClick={handleRefresh} />
                <Tooltip target={'#ActualizarMediciones'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Refrescar datos
                </Tooltip>
              </ToolbarItem>
            </Toolbar>
          </div>
          {/* Grid de Mediciones */}
          <div className='measurements-grid-container'>
            <DataGrid className='grid' dataSource={measurementsDataSource} ref={measurementsGridRef} width={'100%'} hoverStateEnabled={true} height={'100%'} repaintChangesOnly={true} highlightChanges={true} rowAlternationEnabled={true} showColumnLines={true} disabled={!selectedConfiguration}>
              <HeaderFilter visible={true} />
              <Scrolling mode='virtual' useNative={true} scrollByContent={true} showScrollbar='always' />
              <Sorting mode='multiple' />
              <Selection mode='multiple' />
              <Paging enabled={false} />
              <KeyboardNavigation editOnKeyPress={false} enterKeyAction='moveFocus' enterKeyDirection='row' />
              <StateStoring enabled={true} type='custom' />

              <Column allowGrouping={false} allowSorting={false} caption='ID' allowEditing={false} visible={false} dataField='Qcme_Id' width={90} alignment='center' />
              <Column
                allowGrouping={false}
                caption='FECHA/HORA'
                dataField='Qcme_FechaHora'
                width={150}
                alignment='center'
                dataType='datetime'
                format='dd/MM/yyyy HH:mm'
                sortOrder='desc'
                calculateFilterExpression={(filterValue, selectedFilterOperation, target) => {
                  if (target === 'filterRow' || target === 'headerFilter') {
                    // Si hay un valor de filtro con fecha
                    if (filterValue) {
                      const date = new Date(filterValue);
                      date.setHours(0, 0, 0, 0);
                      const nextDate = new Date(date);
                      nextDate.setDate(nextDate.getDate() + 1);

                      // Si la operación es '=' (igual), buscar en el rango del día completo
                      if (selectedFilterOperation === '=' || !selectedFilterOperation) {
                        return [['Qcme_FechaHora', '>=', date], 'and', ['Qcme_FechaHora', '<', nextDate]];
                      }

                      // Para otras operaciones como >, <, >=, <= usar la fecha sin hora
                      if (selectedFilterOperation === '>') {
                        return ['Qcme_FechaHora', '>=', nextDate];
                      }
                      if (selectedFilterOperation === '>=') {
                        return ['Qcme_FechaHora', '>=', date];
                      }
                      if (selectedFilterOperation === '<') {
                        return ['Qcme_FechaHora', '<', date];
                      }
                      if (selectedFilterOperation === '<=') {
                        return ['Qcme_FechaHora', '<', nextDate];
                      }
                    }
                  }
                  // Default filter expression
                  return [['Qcme_FechaHora', selectedFilterOperation || '=', filterValue]];
                }}
              />
              <Column allowGrouping={false} caption='VALOR' dataField='Qcme_Valor' width={100} alignment='center' dataType='number' format='#,##0.00' />
              <Column allowGrouping={false} caption='Z-SCORE' dataField='ZScore' width={120} alignment='center' dataType='number' format='#,##0.00' />
              <Column
                allowGrouping={false}
                caption='ESTADO'
                dataField='Qcme_Estado'
                width={120}
                alignment='center'
                cellRender={renderEstadoCelda}
                calculateFilterExpression={(filterValue, selectedFilterOperation) => {
                  if (filterValue === 'PENDIENTE') {
                    return ['Qcme_Estado', '=', 1];
                  } else if (filterValue === 'VALIDADO') {
                    return ['Qcme_Estado', '=', 2];
                  } else if (filterValue === 'RECHAZADO') {
                    return ['Qcme_Estado', '=', 3];
                  } else if (filterValue === 'ALERTA') {
                    return ['Qcme_Estado', '=', 4];
                  }
                  return ['Qcme_Estado', selectedFilterOperation || '=', filterValue];
                }}>
                <HeaderFilter
                  dataSource={[
                    { text: 'PENDIENTE', value: 'PENDIENTE' },
                    { text: 'VALIDADO', value: 'VALIDADO' },
                    { text: 'RECHAZADO', value: 'RECHAZADO' },
                    { text: 'ALERTA', value: 'ALERTA' },
                  ]}
                />
              </Column>
              <Column
                allowGrouping={false}
                caption='VIOLACIONES'
                dataField='TieneViolaciones'
                width={140}
                alignment='center'
                cellRender={renderViolacionCelda}
                allowFiltering={true}
                allowHeaderFiltering={true}
                calculateFilterExpression={(filterValue, selectedFilterOperation) => {
                  if (filterValue === 'SI') {
                    return ['TieneViolaciones', '=', true];
                  } else if (filterValue === 'NO') {
                    return ['TieneViolaciones', '=', false];
                  }
                  return ['TieneViolaciones', selectedFilterOperation || '=', filterValue];
                }}>
                <HeaderFilter
                  dataSource={[
                    { text: 'SI', value: 'SI' },
                    { text: 'NO', value: 'NO' },
                  ]}
                />
              </Column>
              <Column allowGrouping={false} caption='TURNO' dataField='Qcme_Turno' width={90} alignment='center' />
              <Column allowGrouping={false} caption='COMENTARIO' dataField='Qcme_ComentarioValidacion' minWidth={150} alignment='left' />
            </DataGrid>
          </div>

          {/* Modal de Registro de Medición */}
          <Popup visible={showRegisterModal} onHiding={handleCloseRegisterModal} dragEnabled={false} closeOnOutsideClick={false} showTitle={true} title='Registrar Nueva Medición' width={500} height='auto'>
            <div className='register-measurement-modal'>
              <ValidationGroup ref={validationGroupRef}>
                {/* Nombre del Analito destacado */}
                {selectedConfiguration && (
                  <div className='analito-header'>
                    <h3>{selectedConfiguration.AnalitoNombre}</h3>
                  </div>
                )}

                <div className='modal-section'>
                  <div className='config-summary'>
                    <div className='summary-item'>
                      <span className='label'>Material:</span>
                      <span className='value'>{selectedConfiguration?.MaterialNombre}</span>
                    </div>
                    <div className='summary-item'>
                      <span className='label'>Lote:</span>
                      <span className='value'>{selectedConfiguration?.LoteCodigo}</span>
                    </div>
                  </div>

                  <div className='range-info'>
                    <div className='range-item'>
                      <span className='range-label'>Media Esperada:</span>
                      <span className='range-value'>{selectedConfiguration?.Qcc_MediaEsperada?.toFixed(2)}</span>
                    </div>
                    <div className='range-item'>
                      <span className='range-label'>Rango Aceptable:</span>
                      <span className='range-value'>
                        {((selectedConfiguration?.Qcc_MediaEsperada || 0) - 2 * (selectedConfiguration?.Qcc_DesviacionEst || 0)).toFixed(2)} - {((selectedConfiguration?.Qcc_MediaEsperada || 0) + 2 * (selectedConfiguration?.Qcc_DesviacionEst || 0)).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className='form-field'>
                    <label className='field-label required'>Valor de la Medición:</label>
                    <NumberBox value={medicionValor ?? undefined} onValueChanged={(e) => setMedicionValor(e.value ?? null)} placeholder='Ingrese el valor...' format='#,##0.00' showSpinButtons={false} showClearButton={true} stylingMode='outlined'>
                      <Validator>
                        <RequiredRule message='El valor es requerido' />
                        <RangeRule min={0} message='El valor debe ser mayor a 0' />
                      </Validator>
                    </NumberBox>
                  </div>

                  <div className='form-field'>
                    <label className='field-label'>Observaciones (opcional):</label>
                    <TextArea value={medicionObservacion} onValueChanged={(e) => setMedicionObservacion(e.value || '')} placeholder='Ingrese alguna observación si es necesario...' height={80} maxLength={500} stylingMode='outlined' />
                  </div>
                </div>

                <div className='modal-actions'>
                  <Button text='Cancelar' onClick={handleCloseRegisterModal} stylingMode='outlined' type='normal' disabled={isRegistering} />
                  <Button text={isRegistering ? 'Registrando...' : 'Registrar'} onClick={handleRegisterMeasurement} stylingMode='contained' type='default' icon={isRegistering ? 'refresh' : 'check'} disabled={isRegistering} />
                </div>
              </ValidationGroup>
            </div>
          </Popup>

          {/* Gráfico Levey-Jennings */}
          <div className='chart-container'>
            <div className='chart-header'>
              <h3>Gráfico de Levey-Jennings</h3>
            </div>
            {selectedConfiguration && chartData && chartData.Mediciones && chartData.Mediciones.length > 0 ? (
              <Chart
                dataSource={chartData.Mediciones.map((m: any, index: number) => ({
                  NumeroMedicion: index + 1,
                  Valor: m.Valor,
                  Media: chartData.Media,
                  SD1Positivo: chartData.Linea_1SD_Superior,
                  SD1Negativo: chartData.Linea_1SD_Inferior,
                  SD2Positivo: chartData.Linea_2SD_Superior,
                  SD2Negativo: chartData.Linea_2SD_Inferior,
                  SD3Positivo: chartData.Linea_3SD_Superior,
                  SD3Negativo: chartData.Linea_3SD_Inferior,
                }))}
                height={350}>
                <ArgumentAxis title='Número de Medición' />
                <ValueAxis title='Valor' />
                <CommonSeriesSettings argumentField='NumeroMedicion' type='line' />

                {/* Serie principal de datos */}
                <Series valueField='Valor' name='Mediciones' color='#2196F3' width={2}>
                  <Point size={8} visible={true} />
                </Series>

                {/* Líneas de control */}
                <Series valueField='Media' name='Media' color='#4CAF50' dashStyle='dash' width={2}>
                  <Point visible={false} />
                </Series>
                <Series valueField='SD1Positivo' name='+1 SD' color='#FFC107' dashStyle='dot' width={1}>
                  <Point visible={false} />
                </Series>
                <Series valueField='SD1Negativo' name='-1 SD' color='#FFC107' dashStyle='dot' width={1}>
                  <Point visible={false} />
                </Series>
                <Series valueField='SD2Positivo' name='+2 SD' color='#FF9800' dashStyle='dot' width={1}>
                  <Point visible={false} />
                </Series>
                <Series valueField='SD2Negativo' name='-2 SD' color='#FF9800' dashStyle='dot' width={1}>
                  <Point visible={false} />
                </Series>
                <Series valueField='SD3Positivo' name='+3 SD' color='#F44336' dashStyle='dot' width={1}>
                  <Point visible={false} />
                </Series>
                <Series valueField='SD3Negativo' name='-3 SD' color='#F44336' dashStyle='dot' width={1}>
                  <Point visible={false} />
                </Series>

                <Legend verticalAlignment='bottom' horizontalAlignment='center' />
                <ChartTooltip enabled={true} />
              </Chart>
            ) : (
              <div className='no-chart-data'>
                <p>{selectedConfiguration ? 'No hay datos suficientes para generar el gráfico' : 'Seleccione una configuración para ver el gráfico'}</p>
              </div>
            )}
          </div>
        </ScrollView>
      </div>
    </div>
  );
};

export default MeasurementsQC;
